/**
 * The player: where they are in the real world, and how they move.
 *
 * Position is stored as latitude, longitude and altitude — *not* as local
 * metres. That is the whole point of the design: the authoritative state is
 * geographic, so re-anchoring the ENU frame can never move the player, the HUD
 * can read real coordinates straight off, and the hand-off from another app is
 * just these three numbers.
 */

import type { PerspectiveCamera} from 'three';
import { Euler, Vector3 } from 'three';
import type { PlayerMode, SpawnConfig } from '../config.js';
import { DEG_TO_RAD, EARTH_RADIUS } from '../core/geo/mercator.js';
import type { HeliState } from './Helicopter.js';
import { HELI, HELI_SEAT, HOVER, settleOnGround, stepHelicopter } from './Helicopter.js';
import type { MotoState, TwoWheelerSpec } from './Motorbike.js';
import { MOTO_SPEC, UPRIGHT, stepMotorbike, stepMotorbikeAir } from './Motorbike.js';
import type { DriveState, Powertrain } from './Vehicle.js';
import { CAR_CHASSIS, CAR_TAKEOFF_MARGIN, OFF_ROAD, ROAD, TAKEOFF_LOOKAHEAD, VEHICLE, leavesGround, stepVehicle } from './Vehicle.js';
import type { Chassis } from './Vehicle.js';
import type { Impact } from '../core/vehicle/damage.js';
import { STOPS_DEAD, impactOf, shakeFor } from '../core/vehicle/damage.js';
import { STIFFNESS, crushSpeeds, exchange } from '../core/vehicle/collision.js';
import type { Handling } from '../core/vehicle/handling.js';
import { AS_NEW } from '../core/vehicle/handling.js';
import { snowSurface, wading } from '../core/weather/snow.js';

const METRES_PER_DEG_LAT = DEG_TO_RAD * EARTH_RADIUS;

export interface PlayerState {
  lat: number;
  lon: number;
  /** Metres above sea level. */
  altitude: number;
  mode: PlayerMode;
  /** Metres above the terrain directly below, or undefined if it is unloaded. */
  agl: number | undefined;
  speed: number;
  /** Compass heading in degrees clockwise from north. */
  heading: number;
}

/**
 * Ground elevation at a coordinate, or undefined when that chunk has not
 * streamed in yet.
 *
 * Passed in as a function rather than a single pre-sampled height because a
 * vehicle needs four samples around itself to sit correctly on a slope.
 */
export type GroundSampler = (lat: number, lon: number) => number | undefined;

/** Where the vehicle is and what it is doing, for the renderer to draw it. */
export interface VehiclePose {
  visible: boolean;
  yaw: number;
  forwardSlope: number;
  lateralSlope: number;
  speed: number;
  steerAngle: number;
  /** Along the heading, m/s². */
  accel: number;
  /** Sideways, m/s², positive to the right. */
  lateralAccel: number;
  braking: boolean;
  offRoad: boolean;
  /** In the air, off a crest. */
  airborne: boolean;
  /** Vertical speed at a landing this frame, m/s (negative), else 0. */
  landing: number;
}

/** Where the motorbike is and what it and its rider are doing, for the renderer. */
export interface MotoPose {
  visible: boolean;
  yaw: number;
  speed: number;
  steerAngle: number;
  /** Radians, positive right. */
  lean: number;
  /** Along the heading, m/s². */
  accel: number;
  braking: boolean;
  offRoad: boolean;
  airborne: boolean;
  /** Nose-up in the air, radians. */
  airPitch: number;
  /** Vertical speed at a landing this frame, m/s (negative), else 0. */
  landing: number;
  throttle: number;
  /** On the back wheel, radians. */
  wheelie: number;
  /** The rear sliding. */
  sliding: boolean;
}

/** Whether a coordinate is on a built surface — a road, a bridge, a car park. */
export type SurfaceProbe = (lat: number, lon: number) => boolean;

/**
 * Push a round body of `radius` out of anything solid it overlaps between
 * `feet` and `head` (metres above sea level): returns where it may stand, or
 * undefined when it is clear. Also the unit normal it was pushed along.
 */
export type Collider = (lat: number, lon: number, radius: number, feet: number, head: number) =>
  { lat: number; lon: number; nx: number; nz: number; width?: number; body?: Body } | undefined;

/**
 * Something hit that can be moved: a parked car, a car in traffic. How heavy
 * it is, kg, how fast it was going (scene x, z, m/s), and whatever the
 * collider needs to know it again. Without one, what was hit is fixed: a
 * wall, a tree.
 */
export interface Body {
  mass: number;
  vx: number;
  vz: number;
  /** How it is built, for how stiff it is: see `STIFFNESS`. */
  build: 'car' | 'truck' | 'moto';
  /** Its own stiffness, for a prop (see `core/vehicle/props.ts`), over its build's. */
  stiffness?: number;
  /** For a prop on fixings: the closing speed they hold against, m/s. Slower, it stands as a post does. */
  holds?: number;
  ref: unknown;
}

/** The other side of a hit on a body: what it was, and what the hit did to it. */
export interface Struck {
  body: Body;
  /** Its change in velocity, m/s, along `push`: unit, scene x, z, from the player into it. */
  dv: number;
  /** How hard it is crushed: its equivalent barrier speed, m/s (see `crushSpeeds`). */
  crush: number;
  push: { x: number; z: number };
  /** The impulse, N·s, and where it landed, scene x, z, relative to the player's middle. */
  impulse: number;
  contact: { x: number; z: number };
  /** How high and how wide what struck it is, metres. */
  height: number;
  width: number;
}

/** How quickly the world's own changes under a car are let through, seconds. */
const GROUND_SETTLE = 0.25;
/** And never faster than this, m/s. */
const GROUND_SETTLE_RATE = 1.5;
/** Gravity for bodies in the game, m/s². */
const GRAVITY = 9.81;
/** Take-off speed of a jump, m/s: about a metre up. */
const JUMP_SPEED = 4.5;
/** Share of the steering a body keeps in the air. */
const AIR_CONTROL = 0.3;
/** Ground this far under the feet is walked down onto; further is a drop. */
const STEP_DOWN = 0.45;
/** A body's radius for collisions, metres. */
const BODY_RADIUS = 0.32;
/** The car's collision: two circles along its length. */
const CAR_RADIUS = 0.92;
const CAR_CIRCLE_OFFSET = 1.3;
// A two-wheeler's collision circles, and how it falls in the air (a touch of
// float on the bike, for the long, hanging arcs off a big jump that make
// motocross what it is), are its spec's: see `TwoWheelerSpec`.
/** The steepest climb the bike carries off a lip, as rise per metre: 42°. */
const BIKE_CLIMB = 0.9;
/** How far ahead the bike looks for the ground falling away, metres: near and far. */
const BIKE_LOOKAHEAD = [0.5, 1.0];
/**
 * Just after leaving the ground, how long a body can still be on the last of
 * a lip, s: a take-off judged a moment early rides up what is left of the face
 * and gathers its climb, rather than landing on it.
 */
const LIP_GRACE = 0.12;
/**
 * The most a car (or truck) can be thrown upward, m/s, as a share of its
 * speed and at the most: a road's steepest crest is about 15%, and a car on
 * its springs leaves it at no steeper. 6 m/s is under 2 m of height. The
 * ground's slope under the car as read from the world can be far steeper
 * for a moment, where it catches a bank, a bridge deck's edge or a seam
 * between tiles; taken as it is, it threw cars tens of metres in the air.
 */
const CAR_CLIMB = 0.3;
const CAR_CLIMB_MOST = 6;
/** Up a step this high, metres, a car is at once: a kerb. Higher, it climbs at no more than this many m/s, and this share of its speed. */
const CAR_STEP = 0.25;
const CAR_STEP_RATE = 1.5;
const CAR_RIDE_UP = 0.6;
/**
 * How fast the throttle lifts the nose in the air and the brake drops it,
 * rad/s. The throttle's is gentle: it is held flat out over most jumps, and
 * those should land clean. The brake's is firm, to point it down a landing.
 */
const AIR_NOSE_UP = 0.7;
const AIR_NOSE_DOWN = 1.8;

/**
 * Over this change in speed head-on, m/s (20 km/h), a crash throws the rider
 * off the motorbike, over the bars: see `core/character/ragdoll.ts`.
 */
export const THROWN_OFF = 20 / 3.6;

/**
 * A rider thrown off: the bike's velocity when it struck (scene x up z,
 * m/s), and after; which way it faced, and how far it leant (radians, right
 * positive), for which side it falls.
 */
export interface Throw {
  velocity: { x: number; y: number; z: number };
  after: { x: number; z: number };
  forward: { x: number; z: number };
  yaw: number;
  lean: number;
  /** Off the back, from a landing on the back wheel, rather than over the bars. */
  backward: boolean;
}

/**
 * A bike landing crossed up by more than this, radians (about 46°), from the
 * ground's slope, coming down faster than `CRASH_LANDING_SPEED` m/s, throws
 * its rider: nose first over the bars, tail first off the back.
 */
export const CRASH_LANDING = 0.8;
export const CRASH_LANDING_SPEED = 3;
/**
 * Landing on foot faster than this, m/s, a body crumples rather than lands
 * standing: a drop of about 2.5 m, which is as much as legs take.
 */
export const HARD_LANDING = 7;

/**
 * The autopilot's inputs, each in the keys' own terms and range. Any left out
 * are the keyboard's. See `core/nav/autopilot.ts`.
 */
export interface AutoInput {
  /** Car and motorbike: pedal, -1..1, and wheel, -1 left .. 1 right. */
  throttle?: number;
  steer?: number;
  /** On foot: which way to go, east and north, and whether to run. */
  moveEast?: number;
  moveNorth?: number;
  run?: boolean;
  /** Helicopter: the sticks and the pedals. */
  cyclicPitch?: number;
  cyclicRoll?: number;
  yaw?: number;
  collective?: number;
}

/**
 * A game controller's sticks and triggers (`three/gamepad.ts`), each -1..1,
 * 0 at rest: what the keys ask for, but by how much. A key held wins over
 * the stick for the same control.
 */
export interface PadInput {
  /** The left stick: right, and forward. On foot which way to go; the helicopter's cyclic. */
  moveX: number;
  moveY: number;
  /** Car and motorbike: the pedals (the triggers, forward less back) and the wheel. */
  throttle: number;
  steer: number;
  /** Helicopter: the pedals and the collective. */
  yaw: number;
  collective: number;
}

/**
 * The player's own body, for the renderer to draw.
 *
 * `pose` names which animation family applies; the renderer owns the gait
 * phase, because the phase is purely presentational and advancing it here
 * would couple the controller to the animation's tuning.
 */
export interface AvatarPose {
  visible: boolean;
  pose: 'ground' | 'jump' | 'freefall' | 'canopy';
  /** Altitude of the soles, metres above sea level. */
  feet: number;
  /** Vertical speed, m/s, positive rising: drives the jump pose. */
  vy: number;
  /** Facing, in the sense of `Object3D.rotation.y`. */
  yaw: number;
  /** Speed over the ground, m/s — what the gait is driven by. */
  speed: number;
  /** In freefall, 0 spread belly-down .. 1 head-down in a dive. */
  dive: number;
}

/** What the player's machine and body are doing, for the sound: see `PlayerController.soundState`. */
export interface PlayerSound {
  mode: PlayerMode;
  /** The pedal or twist grip, -1..1; in the helicopter, the collective. */
  throttle: number;
  /** Along the heading, m/s (on foot and falling, the ground speed). */
  speed: number;
  /** The automatic's gear, seconds left of a change, and the throttle as the engine has it (turbo lag). */
  gear: number;
  shifting: number;
  spool: number;
  /** Sideways slide, m/s: tyres scrubbing; `slide` the same signed, positive to the right. */
  slip: number;
  slide: number;
  braking: boolean;
  airborne: boolean;
  offRoad: boolean;
  /** The camera at the driver's eyes, in the cabin. */
  inside: boolean;
  /** Tank or battery empty: the engine stopped. */
  dry: boolean;
  canopy: boolean;
  /** The bike's front wheel up, radians. */
  wheelie: number;
}

/** Where the helicopter is and how it is attitude-wise, for the renderer. */
export interface HelicopterPose {
  visible: boolean;
  yaw: number;
  pitch: number;
  roll: number;
  /** True while the rotor should be turning. */
  running: boolean;
}

/**
 * On-foot speeds, m/s.
 *
 * Human numbers now that there is a human on screen: a brisk walk and a hard
 * run. Anything faster makes the legs look like they are skating, however the
 * gait is tuned — and the car and the helicopter exist for covering ground.
 */
const WALK_SPEED = 2.2;
const RUN_SPEED = 6.4;
/** How quickly the body gets up to speed and stops, m/s². */
const FOOT_ACCELERATION = 11;
/** How quickly the body turns to face where it is going, rad/s. */
const TURN_RATE = 9;
const EYE_HEIGHT = 1.7;
/** The top-down view's closest and furthest, metres over the player. */
const TOP_LOWEST = 25;
const TOP_HIGHEST = 400;
/** Third-person camera: distance behind, and the height it orbits around. */
const FOLLOW_DISTANCE = 4.6;
/** How much further back the wide third-person view sits. */
const FAR_ZOOM = 1.75;
const FOLLOW_TARGET = 1.45;
/** Rough terminal velocity of a falling skydiver, before the canopy opens. */
const FREEFALL_SPEED = 55;
/**
 * Head-down with W held: falling faster, and flying forward much faster —
 * the dive that gets a skydiver somewhere before the canopy has to open.
 */
const DIVE_FALL_SPEED = 85;
const DIVE_GLIDE = 30;
/** Belly-down drift, m/s. */
const FREEFALL_GLIDE = 8;
/** How quickly the body tips into a dive and back out, per second. */
const DIVE_RATE = 2.5;
const CANOPY_SPEED = 6;
const CANOPY_GLIDE = 12;
/** Altitude above ground at which the canopy opens by itself, if the skydiver never dived. */
const CANOPY_ALTITUDE = 400;
/** Radians of turn per pixel of pointer movement. */
const LOOK_SENSITIVITY = 0.0022;

export class PlayerController {
  readonly camera: PerspectiveCamera;

  private lat: number;
  private lon: number;
  private altitude: number;
  private mode: PlayerMode;
  private yaw: number;
  private pitch = -0.22;
  private lastSpeed = 0;
  /** The direction the body faces, which trails the camera's yaw on foot. */
  private bodyYaw: number;
  /** Ground velocity on foot, east and north, m/s. */
  private footEast = 0;
  private footNorth = 0;
  private underCanopy = false;
  /** 0 belly-down .. 1 head-down: eased toward whether W is held. */
  private dive = 0;
  /** The fall's speeds, eased, so a dive gathers pace rather than snapping to it. */
  private fallSpeed = FREEFALL_SPEED;
  /** Kept so the camera can stay above the terrain between updates. */
  private sampler: GroundSampler | undefined;
  private ceilingProbe: ((x: number, z: number, y: number) => number | undefined) | undefined;

  private readonly keys = new Set<string>();
  private readonly euler = new Euler(0, 0, 0, 'YXZ');
  private pointerLocked = false;

  private drive: DriveState = { speed: 0, steerAngle: 0 };
  private moto: MotoState = UPRIGHT;
  /** The bike's pitch in the air, nose-up radians, and the rider's inputs to show. */
  private airPitch = 0;
  private motoThrottle = 0;
  /** The pedal and the collective last step, for the engine's sound. */
  private driveThrottle = 0;
  private heliCollective = 0;
  private sliding = false;
  /** Requested spawn height above ground, until terrain exists to measure from. */
  private pendingSpawnAgl: number | undefined;
  private heli: HeliState = HOVER;
  /** Where the skids met the ground at the last update, as the player's altitude; undefined when unknown. */
  private heliFloor: number | undefined;
  /** Metres per second of climb, surfaced to the HUD. */
  private climbRate = 0;
  /** Where the driver is looking, relative to the car's heading. */
  private lookYaw = 0;
  private forwardSlope = 0;
  private lateralSlope = 0;
  /**
   * Which camera: close behind, further back, first person, or straight down
   * from above, north up. `C` cycles them. `zoom` eases toward the view's
   * distance, so switching glides.
   */
  private view: 'near' | 'far' | 'first' | 'top' = 'near';
  /** How high over the player the top-down camera is, metres. */
  private topHeight = 70;
  private zoom = 1;
  private get chase(): boolean {
    return this.view !== 'first';
  }
  /** Off while an overlay such as the world map has the keyboard and mouse. */
  private inputEnabled = true;
  /** Tells the car whether it is on the road or on the grass. */
  private surfaceProbe: SurfaceProbe | undefined;
  private offRoad = false;
  /** Turn rate last step, rad/s clockwise, for the body's lean. */
  private yawRate = 0;
  private braking = false;
  /**
   * The drive camera's own state, eased toward the car each step: where it
   * looks from, how far back, and the lens. A camera bolted to the car
   * reads as the world swinging round it; one that trails and settles reads
   * as driving.
   */
  private camYaw = 0;
  private camDistance = 7;
  private camFov = 60;
  /** The car's height as the camera follows it: eased, so a bump is not a lurch. */
  private camHeight = 0;
  /** Vertical speed on foot or in the car, m/s, and whether off the ground. */
  private vy = 0;
  /** How far the ground the car follows is off the world's, easing back: see `settleGround`. */
  private groundOffset = 0;
  /** Where the car stood last frame and the ground there then. */
  private groundMark: { lat: number; lon: number; h: number } | undefined;
  private airborne = false;
  /**
   * How fast the car is really rising or falling along its path, m/s: the
   * change in its height, eased, and capped at what a climb could be so a
   * kerb's step is not read as a leap.
   */
  private pathVy = 0;
  private lastPathHeight: number | undefined;
  /** Seconds since the car last landed: it settles before it can fly again. */
  private sinceLanding = 99;
  /** Seconds since leaving the ground, for `LIP_GRACE`. */
  private airTime = 0;
  /** Vertical speed at the last landing, m/s (negative), for the suspension; read once. */
  private landing = 0;
  /**
   * Down: thrown off, tumbling, lying or getting up. The body is the
   * game's to move (`followBody`) until it stands (`standUp`); the keys do
   * nothing but turn the view.
   */
  private down = false;
  /** A throw since last asked: see `takeThrow`. */
  private thrown: Throw | undefined;
  /** In the air because the ground went from under them standing still, not because they stepped off anything. */
  private groundFell = false;
  /** A fall too hard to land standing since last asked: see `takeFall`. */
  private fell: { x: number; y: number; z: number } | undefined;
  /** Hits since last taken. */
  private readonly impacts: Array<{ impact: Impact; velocity: { x: number; z: number }; other?: Struck }> = [];
  /** How heavy the vehicle being driven is, kg, its rider too: for sharing a hit with another. */
  vehicleMass = 1400;
  /** How it is built, for how stiff it is against what it hits. */
  vehicleBuild: 'car' | 'truck' | 'moto' = 'car';
  /** The two-wheeler being ridden, to the physics: the motocrosser's, or the e-scooter's. Set by the caller. */
  twoWheeler: TwoWheelerSpec = MOTO_SPEC;
  /** How the vehicle being driven drives, crashes and all: see `core/vehicle/handling.ts`. */
  handling: Handling = AS_NEW;
  /** A clock for a bent fork's weave. */
  private wobbleClock = 0;
  private readonly heldProps: Array<{ body: Body; closing: number; push: { x: number; z: number } }> = [];
  /** Plants dragging at the vehicle this frame as it pushes through them, m/s². */
  plantDrag = 0;
  /** A crash's jolt to the camera: how far, over how long, how long is left, and a clock to shake by. */
  private shakeAmplitude = 0;
  private shakeSeconds = 0;
  private shakeLeft = 0;
  private shakeClock = 0;
  /** Pushes a body out of what it walks or drives into. See `setCollider`. */
  private collider: Collider | undefined;
  /** The canopy open: pulled on Space or P, or opened by itself low down. */
  private canopyPulled = false;
  /**
   * Dived in this fall (forward pushed, at any time): then the canopy is the
   * skydiver's to open, and with it unopened they hit the ground. A fall
   * never dived opens it by itself at `CANOPY_ALTITUDE`.
   */
  private dived = false;
  /** Height above the ground in freefall, metres, as last seen. */
  private freefallAgl = Infinity;
  /** Seconds since the mouse last moved the view, for easing it back behind. */
  private sinceLook = 99;
  /**
   * Whether V, B and H conjure a car, a bike or a helicopter from nowhere.
   * Off by default: vehicles are found parked in the world, and helicopters
   * on helipads, and got into with E. See `enterVehicle`.
   */
  summonVehicles = false;
  /**
   * The machine's tank or battery is empty: no drive to the wheels, and no
   * lift beyond a slow sink under the rotor. Set by the caller each frame.
   */
  outOfEnergy = false;
  /**
   * Snow lying under the player, metres, after plowing and their own tracks,
   * and whether it is snowing: what the wheels and the feet go through
   * (`core/weather/snow.ts`). Set by the caller each frame.
   */
  snowLying = 0;
  /** The snow behind the wheels: what reversing goes into, mostly the vehicle's own ruts (see `vehicleSnow`). */
  snowLyingBack = 0;
  snowing = false;
  /** What drives the car being driven: electric, petrol or diesel, and its body's weight. Set by the caller. */
  powertrain: Powertrain | undefined;
  /** The machine being driven, to the physics: a car's, or a truck's. See `chassisOf`. */
  chassis: Chassis = CAR_CHASSIS;
  /** How long the whole of it is, trailer and all, metres: how far back the chase camera sits. */
  rigLength: number = VEHICLE.length;
  /**
   * The autopilot's hands on the controls, in place of the keys: pedal and
   * wheel, which way to run, the helicopter's sticks. Set each frame by the
   * caller while it travels on its own; undefined when the player drives.
   */
  autopilot: AutoInput | undefined;
  /** A game controller's sticks, set each frame by `three/gamepad.ts`; at rest when there is none. */
  readonly pad: PadInput = { moveX: 0, moveY: 0, throttle: 0, steer: 0, yaw: 0, collective: 0 };

  /** A control from two keys, `positive` and `negative`, or, with neither held, the controller's `pad` value. */
  private axis(positive: string | readonly string[], negative: string | readonly string[], pad: number): number {
    const held = (k: string | readonly string[]) => (typeof k === 'string' ? this.keys.has(k) : k.some(c => this.keys.has(c)));
    const keys = (held(positive) ? 1 : 0) - (held(negative) ? 1 : 0);
    return keys !== 0 ? keys : pad;
  }

  /** Forward speed of whatever the player is in, m/s: signed on the road, for the autopilot. */
  get forwardSpeed(): number {
    if (this.mode === 'drive') return this.drive.speed;
    if (this.mode === 'moto') return this.moto.speed;
    return this.lastSpeed;
  }

  /** The helicopter's velocity, m/s: east, north, up. */
  get heliVelocity(): { east: number; north: number; up: number } {
    return { east: this.heli.velocity.x, north: -this.heli.velocity.z, up: this.heli.velocity.y };
  }

  /**
   * What the player's machine and body are doing, for the sound: the
   * powertrain's state, slip and air under the wheels, and where the camera
   * is. See `three/audio/`.
   */
  get soundState(): PlayerSound {
    const moto = this.mode === 'moto';
    return {
      mode: this.mode,
      throttle: moto ? this.motoThrottle : this.mode === 'heli' ? this.heliCollective : this.driveThrottle,
      speed: moto ? this.moto.speed : this.mode === 'drive' ? this.drive.speed : this.lastSpeed,
      gear: this.drive.gear ?? 1,
      shifting: this.drive.shifting ?? 0,
      spool: this.drive.spool ?? 0,
      slip: Math.abs((moto ? this.moto.lateral : this.drive.lateral) ?? 0),
      slide: (moto ? this.moto.lateral : this.drive.lateral) ?? 0,
      braking: this.braking,
      airborne: this.airborne,
      offRoad: this.offRoad,
      inside: !this.chase,
      dry: this.outOfEnergy,
      canopy: this.underCanopy,
      wheelie: moto ? this.moto.wheelie ?? 0 : 0,
    };
  }

  /** The car's gear for the instruments: 'D' for an electric one, else the automatic's gear. */
  get gear(): string {
    if (this.powertrain?.kind === 'electric') return 'D';
    if (this.drive.speed < -0.2) return 'R';
    return String(this.drive.gear ?? 1);
  }
  /** Where the driver's eyes are in the car being driven: across, up, back. */
  private carSeat: { side: number; eye: number; back: number } = { side: VEHICLE.seatOffset, eye: VEHICLE.eyeHeight, back: 0 };

  constructor(camera: PerspectiveCamera, spawn: SpawnConfig, private readonly domElement: HTMLElement) {
    this.camera = camera;
    this.lat = spawn.lat;
    this.lon = spawn.lon;
    this.mode = spawn.mode;
    // The spawn altitude is given *above ground*, but no terrain has streamed
    // in yet, so the ground height is unknown. Hold the value until the first
    // real sample arrives and resolve it then — see `resolveSpawnAltitude`.
    // Treating it as an absolute altitude instead silently changes what the
    // caller asked for, and puts any spawn below the local terrain height
    // underground.
    this.altitude = spawn.altitudeAgl;
    this.pendingSpawnAgl = spawn.altitudeAgl > 0 ? spawn.altitudeAgl : undefined;
    // Compass heading is clockwise from north; scene yaw is counter-clockwise
    // from -Z (north), hence the negation.
    this.yaw = -spawn.heading * DEG_TO_RAD;
    this.bodyYaw = this.yaw;
    this.bindInput();
  }

  get state(): PlayerState {
    return {
      lat: this.lat,
      lon: this.lon,
      altitude: this.altitude,
      mode: this.mode,
      agl: undefined,
      speed: this.lastSpeed,
      heading: ((-this.yaw * (180 / Math.PI)) % 360 + 360) % 360,
    };
  }

  get helicopterPose(): HelicopterPose {
    return {
      visible: this.mode === 'heli',
      yaw: this.yaw,
      pitch: this.heli.pitch,
      roll: this.heli.roll,
      running: this.mode === 'heli',
    };
  }

  /** Ground velocity, scene x and z, m/s: on foot as walked, in a vehicle along its heading. */
  get velocity(): { x: number; z: number } {
    if (this.mode === 'walk') return { x: this.footEast, z: -this.footNorth };
    const ahead = this.mode === 'drive' ? this.drive.speed : this.mode === 'moto' ? this.moto.speed : this.lastSpeed;
    return { x: -Math.sin(this.yaw) * ahead, z: -Math.cos(this.yaw) * ahead };
  }

  get avatarPose(): AvatarPose {
    const onFoot = this.mode === 'walk';
    const falling = this.mode === 'freefall';
    return {
      // On the bike the rider is posed by the bike; see `motoPose`.
      visible: (onFoot || falling || this.mode === 'moto') && this.chase,
      pose: onFoot ? (this.airborne ? 'jump' : 'ground') : this.underCanopy ? 'canopy' : 'freefall',
      feet: this.altitude - EYE_HEIGHT,
      vy: this.vy,
      yaw: this.bodyYaw,
      speed: onFoot ? Math.hypot(this.footEast, this.footNorth) : 0,
      dive: falling && !this.underCanopy ? this.dive : 0,
    };
  }

  /**
   * Altitude of whatever touches the ground — soles, tyres or skids. The
   * ground sampler uses it to decide which built surfaces are reachable: a
   * bridge deck at shin height is stepped onto, one overhead is walked under.
   */
  get groundContact(): number {
    if (this.mode === 'drive' || this.mode === 'moto') return this.altitude;
    if (this.mode === 'heli') return this.altitude - HELI.skidHeight;
    return this.altitude - EYE_HEIGHT;
  }

  /** Told of every key pressed while input is enabled, for actions the controller does not own. */
  onPress: ((code: string) => boolean | void) | undefined;

  /**
   * Turn to face `yaw` (in the sense of `Object3D.rotation.y`), camera and
   * all — only while at a standstill, where a turn on the spot is no jolt.
   * Returns whether it turned.
   */
  faceIfStill(yaw: number): boolean {
    if (this.lastSpeed > 1.5 || this.airborne) return false;
    this.yaw = yaw;
    this.camYaw = yaw;
    this.bodyYaw = yaw;
    this.lookYaw = 0;
    return true;
  }

  /** Whether the camera is behind the player rather than at their eyes. */
  get thirdPerson(): boolean {
    return this.chase;
  }

  /** Whether the keyboard and mouse are the player's, not an overlay's. */
  get acceptsInput(): boolean {
    return this.inputEnabled;
  }

  /** Hand the keyboard and mouse to an overlay, or take them back. */
  setInputEnabled(enabled: boolean): void {
    this.inputEnabled = enabled;
    if (!enabled) this.keys.clear();
  }

  /**
   * Go somewhere else in the world, arriving on foot at that spot — or by
   * parachute, when given a height to drop from.
   *
   * The ground at the destination has not streamed in yet, so the height is
   * held as a pending above-ground altitude — exactly like the spawn — and
   * resolved when the first terrain sample arrives. Until then a drop hangs in
   * the air rather than falling through a hole in the world; on foot, the
   * walk snaps up onto the ground as soon as it is known.
   */
  teleport(lat: number, lon: number, heightAboveGround = 0): void {
    this.lat = Math.max(-85, Math.min(85, lat));
    this.lon = ((lon + 540) % 360) - 180;
    this.mode = heightAboveGround > 0 ? 'freefall' : 'walk';
    this.pendingSpawnAgl = heightAboveGround;
    // A placeholder the level-of-detail choice reads as "high up" (or on the
    // ground) until the real ground height is known.
    this.altitude = heightAboveGround;
    this.underCanopy = false;
    this.canopyPulled = false;
    this.dived = false;
    this.dive = 0;
    this.fallSpeed = FREEFALL_SPEED;
    this.airborne = false;
    this.vy = 0;
    this.footEast = 0;
    this.footNorth = 0;
    this.drive = { speed: 0, steerAngle: 0 };
    this.moto = UPRIGHT;
    this.heli = HOVER;
    this.climbRate = 0;
    this.lastSpeed = 0;
    this.lookYaw = 0;
    this.down = false;
    this.thrown = undefined;
    this.fell = undefined;
  }

  /** Vertical speed in m/s, positive climbing. Only meaningful when flying. */
  get verticalSpeed(): number {
    return this.climbRate;
  }

  /** Where the car is and which way it faces, to put it back with `holdDrive`. */
  get drivePlace(): { lat: number; lon: number; yaw: number } {
    return { lat: this.lat, lon: this.lon, yaw: this.yaw };
  }

  /**
   * Put the car back where it was, stopped: what it pulls has run into
   * something, and the whole rig stands until it is backed or steered clear.
   */
  holdDrive(place: { lat: number; lon: number; yaw: number }): void {
    this.lat = place.lat;
    this.lon = place.lon;
    this.yaw = place.yaw;
    this.drive = { ...this.drive, speed: 0, lateral: 0 };
    this.yawRate = 0;
  }

  get vehiclePose(): VehiclePose {
    return {
      visible: this.mode === 'drive',
      yaw: this.yaw,
      forwardSlope: this.forwardSlope,
      lateralSlope: this.lateralSlope,
      speed: this.drive.speed,
      steerAngle: this.drive.steerAngle,
      accel: this.drive.accel ?? 0,
      lateralAccel: this.drive.speed * this.yawRate,
      braking: this.braking,
      offRoad: this.offRoad,
      airborne: this.airborne,
      landing: this.mode === 'drive' ? this.takeLanding() : 0,
    };
  }

  get motoPose(): MotoPose {
    return {
      visible: this.mode === 'moto',
      yaw: this.yaw,
      speed: this.moto.speed,
      steerAngle: this.moto.steerAngle,
      lean: this.moto.lean,
      accel: this.moto.accel ?? 0,
      braking: this.braking,
      offRoad: this.offRoad,
      airborne: this.airborne,
      airPitch: this.airPitch,
      landing: this.mode === 'moto' ? this.takeLanding() : 0,
      throttle: this.motoThrottle,
      wheelie: this.moto.wheelie ?? 0,
      sliding: this.sliding,
    };
  }

  /**
   * A roof over scene (x, z) for something at height y, a tunnel's ceiling,
   * which the cameras stay under. Undefined in the open.
   */
  setCeilingProbe(probe: (x: number, z: number, y: number) => number | undefined): void {
    this.ceilingProbe = probe;
  }

  /** A camera height held under any roof over it, for a body at height `body`. */
  private underRoof(x: number, z: number, y: number, body: number): number {
    const roof = this.ceilingProbe?.(x, z, body);
    return roof === undefined ? y : Math.min(y, roof - 0.4);
  }

  /** Where the car can tell road from grass. */
  setSurfaceProbe(probe: SurfaceProbe): void {
    this.surfaceProbe = probe;
  }

  /** What bodies collide with: buildings, trees, posts. */
  setCollider(collider: Collider): void {
    this.collider = collider;
  }

  /** Push out of whatever the body at `radius` overlaps; returns the push normal, if any. */
  private collide(radius: number, feet: number, head: number, forwardOffset = 0): { nx: number; nz: number; width: number; body?: Body } | undefined {
    if (this.collider === undefined) return undefined;
    const f = this.horizontalForward();
    // Offset along the heading (east, north): scene forward (x, z) is (east, -north).
    const oe = f.x * forwardOffset, on = -f.z * forwardOffset;
    const cosLat = Math.max(1e-6, Math.cos(this.lat * DEG_TO_RAD));
    const lat = this.lat + on / METRES_PER_DEG_LAT, lon = this.lon + oe / (METRES_PER_DEG_LAT * cosLat);
    const out = this.collider(lat, lon, radius, feet, head);
    if (out === undefined) return undefined;
    this.lat += out.lat - lat;
    this.lon += out.lon - lon;
    return { nx: out.nx, nz: out.nz, width: out.width ?? Infinity, body: out.body };
  }

  /**
   * A hit this frame: from `before` (scene x, z velocity) to `speed` along
   * `forward`, pushed along the hit's normal at the circle `offset` metres
   * ahead of the middle, of `radius`, struck at `height` over the ground.
   * Kept for the damage (`takeImpacts`), and felt through the camera.
   */
  private struck(
    before: { x: number; z: number }, speed: number, forward: { x: number; z: number },
    hit: { nx: number; nz: number; width: number }, offset: number, radius: number, height: number,
    after = { x: forward.x * speed, z: forward.z * speed }, other?: Struck, crushSpeed?: number,
  ): void {
    const contact = { x: forward.x * offset - hit.nx * radius, z: forward.z * offset - hit.nz * radius };
    const impact = impactOf(before, after, { x: hit.nx, z: hit.nz }, contact, forward, height, hit.width);
    if (impact.dv < 0.3 && (other === undefined || other.dv < 0.3)) return;
    // The jolt is the change in speed; the crush, against another vehicle, its share of the energy.
    const jolt = shakeFor(impact.dv);
    if (crushSpeed !== undefined) impact.dv = crushSpeed;
    this.impacts.push({ impact, velocity: before, other });
    if (jolt.amplitude > this.shakeAmplitude * (this.shakeLeft / Math.max(1e-3, this.shakeSeconds))) {
      this.shakeAmplitude = jolt.amplitude;
      this.shakeSeconds = this.shakeLeft = jolt.seconds;
    }
  }

  /**
   * A hit on a body that can move, another vehicle: momentum is shared along
   * the hit's normal by the two weights (`exchange`), so a light car is
   * thrown and a heavy one barely slows. Returns the player's velocity after
   * (scene x, z), or undefined when the two were not closing. The other's
   * share goes with the impact, for the game to move and crush it.
   */
  private shared(
    before: { x: number; z: number }, hit: { nx: number; nz: number; width: number; body?: Body },
    offset: number, radius: number, height: number, width: number,
  ): { x: number; z: number } | undefined {
    const body = hit.body!;
    // Closing along the normal, which points out of it toward the player.
    const closing = (body.vx - before.x) * hit.nx + (body.vz - before.z) * hit.nz;
    const { dv1, dv2, impulse } = exchange(this.vehicleMass, body.mass, closing);
    if (impulse <= 0) return undefined;
    // Crushed by the energy each takes, by weight and stiffness, not only by its change in speed.
    const crush = crushSpeeds(this.vehicleMass, body.mass, closing, STIFFNESS[this.vehicleBuild], body.stiffness ?? STIFFNESS[body.build]);
    const after = { x: before.x + hit.nx * dv1, z: before.z + hit.nz * dv1 };
    const forward = this.horizontalForward();
    const contact = { x: forward.x * offset - hit.nx * radius, z: forward.z * offset - hit.nz * radius };
    this.struck(before, 0, forward, hit, offset, radius, height, after, {
      body, dv: dv2, crush: crush.second, push: { x: -hit.nx, z: -hit.nz }, impulse, contact, height, width,
    }, crush.first);
    return after;
  }

  /**
   * Whether a prop's fixings hold against this hit: closing slower than
   * they hold, it stands and is hit as a post is. Kept for the game to bend
   * it by how near it came to giving.
   */
  private holds(hit: { nx: number; nz: number; body?: Body }, before: { x: number; z: number }): boolean {
    const body = hit.body!;
    if (body.holds === undefined) return false;
    const closing = (body.vx - before.x) * hit.nx + (body.vz - before.z) * hit.nz;
    if (closing >= body.holds) return false;
    if (closing > 0.3) this.heldProps.push({ body, closing, push: { x: -hit.nx, z: -hit.nz } });
    return true;
  }

  /** Props whose fixings held against a hit since last asked: how hard, and which way it pushed them. */
  takeHeld(): Array<{ body: Body; closing: number; push: { x: number; z: number } }> {
    return this.heldProps.splice(0, this.heldProps.length);
  }

  /** The hits since last asked, with the velocity each came at (scene x, z): see `core/vehicle/damage.ts`. */
  takeImpacts(): Array<{ impact: Impact; velocity: { x: number; z: number }; other?: Struck }> {
    return this.impacts.splice(0, this.impacts.length);
  }

  /**
   * Whether a hit from `before` to `after` (scene x, z, m/s) on a bike
   * facing `forward` throws its rider off: over `THROWN_OFF` of change in
   * speed head-on. If it does, the rider is down, and the throw is kept for
   * the game to start the body tumbling (`takeThrow`).
   */
  private throwsRider(before: { x: number; z: number }, after: { x: number; z: number }, forward: { x: number; z: number }): boolean {
    const headOn = (before.x - after.x) * forward.x + (before.z - after.z) * forward.z;
    if (headOn < THROWN_OFF) return false;
    this.throwOff(before, this.airborne ? this.vy : 0, after, forward, false);
    return true;
  }

  /** The rider off the bike, going at `before` (and `vy` up) when it stopped, the bike left going at `after`. */
  private throwOff(before: { x: number; z: number }, vy: number, after: { x: number; z: number }, forward: { x: number; z: number }, backward: boolean): void {
    this.thrown = {
      velocity: { x: before.x, y: vy, z: before.z },
      after: { ...after }, forward: { x: forward.x, z: forward.z }, yaw: this.yaw, lean: this.moto.lean, backward,
    };
    this.goDown();
  }

  /** Down: on foot, still, the keys idle until the body stands. */
  private goDown(): void {
    this.mode = 'walk';
    this.down = true;
    this.airborne = false;
    this.vy = 0;
    this.footEast = 0;
    this.footNorth = 0;
    this.moto = UPRIGHT;
    this.airPitch = 0;
    this.lookYaw = 0;
    this.lastSpeed = 0;
    this.bodyYaw = this.yaw;
  }

  /** The rider thrown off since last asked, if any. */
  takeThrow(): Throw | undefined {
    const t = this.thrown;
    this.thrown = undefined;
    return t;
  }

  /** A fall on foot since last asked, too hard to land standing: the body's velocity, scene x up z, m/s. */
  takeFall(): { x: number; y: number; z: number } | undefined {
    const f = this.fell;
    this.fell = undefined;
    return f;
  }

  /** Knocked down on foot (by a vehicle, say): true if they were on their feet to be. */
  knockDown(): boolean {
    if (this.mode !== 'walk' || this.down) return false;
    this.goDown();
    return true;
  }

  /**
   * The vehicle being driven struck something light, a person, and changed
   * velocity by `dv` (scene x, z, m/s): slowed, jolted, and on a motorbike,
   * hit hard enough, its rider thrown.
   */
  bump(dv: { x: number; z: number }): void {
    if (this.mode !== 'drive' && this.mode !== 'moto') return;
    const f = this.horizontalForward();
    const jolt = shakeFor(Math.hypot(dv.x, dv.z));
    if (jolt.amplitude > this.shakeAmplitude * (this.shakeLeft / Math.max(1e-3, this.shakeSeconds))) {
      this.shakeAmplitude = jolt.amplitude;
      this.shakeSeconds = this.shakeLeft = jolt.seconds;
    }
    const along = dv.x * f.x + dv.z * f.z;
    if (this.mode === 'drive') {
      this.drive = { ...this.drive, speed: this.drive.speed + along };
      return;
    }
    const side = this.moto.lateral ?? 0;
    const before = { x: f.x * this.moto.speed - f.z * side, z: f.z * this.moto.speed + f.x * side };
    this.moto = { ...this.moto, speed: this.moto.speed + along };
    this.throwsRider(before, { x: before.x + dv.x, z: before.z + dv.z }, f);
  }

  /** Thrown off and not yet back on their feet. */
  get isDown(): boolean {
    return this.down;
  }

  /** Down, the body is where the game says: at (lat, lon), its lowest at `feet`, for the camera to follow. */
  followBody(lat: number, lon: number, feet: number): void {
    if (!this.down) return;
    this.lat = lat;
    this.lon = lon;
    this.altitude = feet + EYE_HEIGHT;
  }

  /** Back on their feet at (lat, lon), the soles at `feet`, facing `bodyYaw`: walking again. */
  standUp(lat: number, lon: number, feet: number, bodyYaw: number): void {
    if (!this.down) return;
    this.down = false;
    this.lat = lat;
    this.lon = lon;
    this.altitude = feet + EYE_HEIGHT;
    this.bodyYaw = bodyYaw;
    this.airborne = false;
    this.vy = 0;
  }

  private takeLanding(): number {
    const l = this.landing;
    this.landing = 0;
    return l;
  }

  /** Feed the world the viewer state it needs to choose detail. */
  viewerState(groundHeight: number | undefined): { lat: number; lon: number; altitude: number } {
    const agl = groundHeight === undefined ? this.altitude : this.altitude - groundHeight;
    return { lat: this.lat, lon: this.lon, altitude: Math.max(0, agl) };
  }

  /**
   * Advance by `dt` seconds.
   *
   * `sample` returns terrain elevation at a coordinate, or undefined when that
   * chunk has not streamed in yet — in which case the player is held in place
   * vertically rather than dropped through a hole in the world. It is a
   * function rather than a single height because a vehicle needs four samples
   * around itself to sit correctly on a slope.
   */
  update(dt: number, sample: GroundSampler): void {
    this.sampler = sample;
    this.shakeClock += dt;
    this.shakeLeft = Math.max(0, this.shakeLeft - dt);
    const wantZoom = this.view === 'far' ? FAR_ZOOM : 1;
    this.zoom += (wantZoom - this.zoom) * (1 - Math.exp(-dt * 5));
    const groundHeight = sample(this.lat, this.lon);
    this.resolveSpawnAltitude(groundHeight);

    if (this.down) {
      this.lastSpeed = 0;
      this.syncCamera();
      return;
    }

    if (this.mode === 'drive' || this.mode === 'moto') {
      // The ground under the wheels as it settles: see `settleGround`.
      const settled = this.settleGround(sample, groundHeight, dt);
      const smooth: GroundSampler = this.groundOffset === 0 ? sample : (la, lo) => {
        const h = sample(la, lo);
        return h === undefined ? undefined : h + this.groundOffset;
      };
      if (this.mode === 'drive') this.updateDrive(dt, smooth, settled);
      else this.updateMoto(dt, smooth, settled);
      this.syncCamera();
      return;
    }
    this.groundOffset = 0;
    this.groundMark = undefined;

    if (this.mode === 'heli') {
      this.updateHelicopter(dt, groundHeight);
      this.syncCamera();
      return;
    }

    const forward = this.horizontalForward();
    const right = new Vector3(-forward.z, 0, forward.x);

    const move = new Vector3();
    if (this.keys.has('KeyW')) move.add(forward);
    if (this.keys.has('KeyS')) move.sub(forward);
    if (this.keys.has('KeyD')) move.add(right);
    if (this.keys.has('KeyA')) move.sub(right);
    // A controller's stick, with no key held: by how far it is pushed.
    if (move.lengthSq() === 0) move.addScaledVector(forward, this.pad.moveY).addScaledVector(right, this.pad.moveX);
    // On the autopilot, the way to run is given in the world, not by the camera.
    const auto = this.autopilot;
    if (auto !== undefined && this.mode === 'walk') move.set(auto.moveEast ?? 0, 0, -(auto.moveNorth ?? 0));
    if (move.lengthSq() > 1) move.normalize();

    if (this.mode === 'freefall') {
      // Still waiting for the ground under a new drop: hold position.
      if (this.pendingSpawnAgl !== undefined) {
        this.syncCamera();
        return;
      }
      this.updateFreefall(dt, move, groundHeight);
    } else {
      this.updateWalk(dt, move, groundHeight);
    }

    this.syncCamera();
  }

  /**
   * Turn the requested above-ground spawn height into an absolute altitude,
   * the first time the terrain underneath is known.
   */
  /**
   * The world's own changes under a car or a bike, eased in. A tile streamed
   * in, or rebuilt on finer terrain, can put the road it carries a few tens
   * of centimetres higher or lower than the one it replaces, all at once:
   * followed exactly, the car leapt up out of the road or dropped and took
   * off. So where the car stood last frame is sampled again: if the ground
   * there has moved though the car has not, the difference is carried as
   * an offset that dies away over a quarter of a second. Kerbs, crests and
   * steps along the road are differences in space, not in time, and are left
   * as they are.
   */
  private settleGround(sample: GroundSampler, ground: number | undefined, dt: number): number | undefined {
    const mark = this.groundMark;
    // A teleport or a respawn is a new place, not a settling road.
    const moved = mark === undefined ? Infinity
      : Math.hypot(mark.lat - this.lat, (mark.lon - this.lon) * Math.cos(this.lat * DEG_TO_RAD)) * METRES_PER_DEG_LAT;
    if (moved > 20) {
      this.groundOffset = 0;
    } else {
      const now = sample(mark!.lat, mark!.lon);
      if (now !== undefined && Math.abs(now - mark!.h) > 0.02) this.groundOffset -= now - mark!.h;
    }
    // Let through over a quarter of a second, and never faster than a car's
    // springs would rise or settle: a glimpse of the ground metres off, as a
    // tile swaps in a moment before its roads are cut into it, comes and goes
    // with the car barely stirring.
    const ease = Math.min(Math.abs(this.groundOffset) * (1 - Math.exp(-dt / GROUND_SETTLE)), GROUND_SETTLE_RATE * dt);
    this.groundOffset -= Math.sign(this.groundOffset) * ease;
    if (Math.abs(this.groundOffset) < 0.005) this.groundOffset = 0;
    this.groundMark = ground === undefined ? undefined : { lat: this.lat, lon: this.lon, h: ground };
    return ground === undefined ? undefined : ground + this.groundOffset;
  }

  private resolveSpawnAltitude(groundHeight: number | undefined): void {
    if (this.pendingSpawnAgl === undefined || groundHeight === undefined) return;
    this.altitude = groundHeight + this.pendingSpawnAgl;
    this.pendingSpawnAgl = undefined;
  }

  private updateHelicopter(dt: number, groundHeight: number | undefined): void {
    const auto = this.autopilot;
    const cyclicPitch = auto?.cyclicPitch ?? this.axis('KeyW', 'KeyS', this.pad.moveY);
    const cyclicRoll = auto?.cyclicRoll ?? this.axis('KeyD', 'KeyA', this.pad.moveX);
    const yawInput = auto?.yaw ?? this.axis('KeyE', 'KeyQ', this.pad.yaw);
    // Dry tanks: the engine stops, and the rotor only slows the fall.
    const collective = this.outOfEnergy ? -0.35 : auto?.collective ?? this.axis('Space', ['ShiftLeft', 'ShiftRight'], this.pad.collective);

    this.heliCollective = collective;
    const step = stepHelicopter(
      this.heli,
      { cyclicPitch, cyclicRoll, yaw: yawInput, collective },
      this.yaw,
      dt,
    );
    this.heli = step.state;
    this.yaw += step.yawDelta;

    // Scene velocity is (x east, y up, z south); translate() wants east/north.
    this.translate(this.heli.velocity.x * dt, -this.heli.velocity.z * dt);
    this.altitude += this.heli.velocity.y * dt;

    // Skids meet the ground. Clamping here rather than in the flight model
    // keeps the model pure — it has no idea the world has terrain in it.
    const floor = groundHeight === undefined ? undefined : groundHeight + HELI.skidHeight;
    this.heliFloor = floor;
    if (floor !== undefined && this.altitude <= floor) {
      this.altitude = floor;
      this.heli = settleOnGround(this.heli, dt);
    }

    this.climbRate = this.heli.velocity.y;
    this.lastSpeed = Math.hypot(this.heli.velocity.x, this.heli.velocity.y, this.heli.velocity.z);
  }

  /**
   * In freefall with the canopy still packed: whether it will open by
   * itself, and how high above the ground the skydiver is, metres.
   * Undefined otherwise.
   */
  get packedCanopy(): { opensItself: boolean; agl: number } | undefined {
    if (this.mode !== 'freefall' || this.canopyPulled || this.pendingSpawnAgl !== undefined) return undefined;
    return { opensItself: !this.dived, agl: this.freefallAgl };
  }

  /** Whether a key is held down now. */
  held(code: string): boolean {
    return this.keys.has(code);
  }

  /**
   * The throttle: W forward, S back. With the tank empty, only braking —
   * S against the way it is rolling — does anything.
   */
  private throttleInput(speed: number): number {
    const t = this.autopilot?.throttle ?? this.axis('KeyW', 'KeyS', this.pad.throttle);
    // A wrecked engine, or a torn wheel, will not drive it: only the brakes do anything.
    if (!this.outOfEnergy && this.handling.dead === undefined) return t * this.handling.power;
    return speed > 0.5 ? Math.min(0, t) : speed < -0.5 ? Math.max(0, t) : 0;
  }

  /** A speed slowed by the plants it is pushing through, never past a stop. */
  private dragged(speed: number, dt: number): number {
    if (this.plantDrag <= 0) return speed;
    return Math.sign(speed) * Math.max(0, Math.abs(speed) - this.plantDrag * dt);
  }

  /** Steering as asked, and as a crash has bent it: a pull to one side, and a weave on bent forks. */
  private steerInput(speed: number, dt: number): number {
    const asked = this.autopilot?.steer ?? this.axis('KeyD', 'KeyA', this.pad.steer);
    const h = this.handling;
    if (h.pull === 0 && h.wobble === 0) return asked;
    this.wobbleClock += dt;
    const moving = Math.min(1, Math.abs(speed) / 8);
    const weave = h.wobble * 0.25 * Math.sin(this.wobbleClock * (5 + Math.abs(speed) * 0.2)) * moving;
    return Math.max(-1, Math.min(1, asked + (h.pull + weave) * moving));
  }

  /** The ground's grip, drag and top speed with the snow on it: see `snowSurface`. */
  private snowy(surface: { grip: number; rolling: number; topSpeed: number }, reversing: boolean): typeof surface {
    const lying = reversing ? this.snowLyingBack : this.snowLying;
    if (lying <= 0 && !this.snowing) return surface;
    return snowSurface(surface, lying, this.vehicleBuild, !this.offRoad, this.snowing);
  }

  /** The ground's grip, drag and top speed, as a crash has left the vehicle to use them. */
  private damaged(surface: { grip: number; rolling: number; topSpeed: number }): typeof surface {
    const h = this.handling;
    if (h === AS_NEW) return surface;
    return { grip: surface.grip * h.grip, rolling: surface.rolling * h.drag, topSpeed: surface.topSpeed * h.topSpeed };
  }

  private updateDrive(dt: number, sample: GroundSampler, groundHeight: number | undefined): void {
    const throttle = this.throttleInput(this.drive.speed);
    const steer = this.steerInput(this.drive.speed, dt);
    this.driveThrottle = throttle;

    const ch = this.chassis;
    const car = ch === CAR_CHASSIS;
    this.measureSlopes(sample, dt, ch.wheelbase * 0.6, car ? VEHICLE.width * 0.75 : ch.width * 0.45);
    this.offRoad = this.surfaceProbe !== undefined && !this.surfaceProbe(this.lat, this.lon);
    const handbrake = this.keys.has('Space');

    // In the air the wheels have nothing to push or steer against: the car
    // flies on its momentum, only the air slowing it.
    const result = this.airborne
      ? {
        state: { ...this.drive, speed: this.drive.speed * (1 - 0.00032 * Math.abs(this.drive.speed) * dt), accel: 0 },
        yawDelta: 0,
      }
      : stepVehicle(
        this.drive,
        { throttle, steer, handbrake },
        this.forwardSlope,
        dt,
        this.damaged(this.snowy(this.offRoad ? OFF_ROAD : ROAD, this.drive.speed < -0.1 || (throttle < 0 && this.drive.speed <= 0.2))),
        this.powertrain,
        ch,
      );
    this.drive = result.state;
    this.drive = { ...this.drive, speed: this.dragged(this.drive.speed, dt) };
    this.braking = handbrake || (throttle < 0 && this.drive.speed > 0.2);
    this.yawRate = dt > 0 ? result.yawDelta / dt : 0;
    // Scene yaw runs counter-clockwise while steering input is
    // clockwise-positive, so the sign flips here.
    this.yaw -= result.yawDelta;

    // Along the heading, plus any sideways slide. Right of forward (x, z) is (-z, x).
    const forward = this.horizontalForward();
    const lateral = this.drive.lateral ?? 0;
    const east = forward.x * this.drive.speed - forward.z * lateral;
    const southward = forward.z * this.drive.speed + forward.x * lateral;
    this.translate(east * dt, -southward * dt);

    // Walls and trees: two circles along the car. The velocity into what it
    // hit goes, as a crumple and a scrape rather than a bounce off a spring.
    // A car is two circles; a truck a row of them its width, from nose to tail, and tall.
    const radius = car ? CAR_RADIUS : ch.width / 2;
    const span = car ? CAR_CIRCLE_OFFSET : ch.length / 2 - radius;
    const circles = car ? 2 : Math.max(2, Math.ceil((2 * span) / radius) + 1);
    const top = car ? 1.5 : 3.4;
    for (let i = 0; i < circles; i++) {
      const offset = span - (2 * span * i) / (circles - 1);
      const hit = this.collide(radius, this.altitude + 0.3, this.altitude + top, offset);
      if (hit === undefined) continue;
      if (hit.body !== undefined) {
        // Another vehicle, or a prop come free of its fixings: the hit is shared by weight.
        const side = this.drive.lateral ?? 0;
        const before = { x: forward.x * this.drive.speed - forward.z * side, z: forward.z * this.drive.speed + forward.x * side };
        if (!this.holds(hit, before)) {
          const after = this.shared(before, hit, offset, radius, car ? 0.5 : 0.85, ch.width);
          if (after !== undefined) {
            this.drive = { ...this.drive, speed: after.x * forward.x + after.z * forward.z, lateral: -after.x * forward.z + after.z * forward.x };
          }
          continue;
        }
      }
      // Forward in scene axes is (fx, fz); the normal points out of the obstacle.
      const into = (forward.x * hit.nx + forward.z * hit.nz) * Math.sign(this.drive.speed || 1);
      if (into < 0) {
        const side = this.drive.lateral ?? 0;
        const before = { x: forward.x * this.drive.speed - forward.z * side, z: forward.z * this.drive.speed + forward.x * side };
        // Hard into it, it stops dead with a hand's breadth of rebound: a
        // crumple zone takes the speed, not a spring. Otherwise head-on
        // keeps little of its speed, a glancing blow most of it.
        const hard = -into * Math.abs(this.drive.speed) >= STOPS_DEAD;
        this.drive = { ...this.drive, speed: hard ? -this.drive.speed * 0.04 : this.drive.speed * (1 + into * 0.85), lateral: 0 };
        this.struck(before, this.drive.speed, forward, hit, offset, radius, car ? 0.5 : 0.85);
      }
    }

    this.lastSpeed = Math.hypot(this.drive.speed, lateral);
    if (groundHeight !== undefined && this.flyOrFollow(groundHeight, dt, sample, this.drive.speed)) {
      // A hard landing scrubs some speed.
      const hard = Math.min(1, Math.max(0, -this.landing - 3) / 10);
      this.drive = { ...this.drive, speed: this.drive.speed * (1 - 0.15 * hard) };
    }
    this.easeDriveCamera(dt);
  }

  /**
   * The motorbike. On the ground it leans into its turns and rides up on its
   * back wheel on the throttle (`stepMotorbike`); Space stamps on the rear
   * brake and the back comes round, for a sharp turn. In the air the
   * throttle and brake pitch it — the spinning rear wheel's gyroscope — and
   * A/D whip it sideways. Land it level with the ground and it rides on;
   * land it crossed up, nose- or tail-heavy, and the landing takes speed off it.
   */
  private updateMoto(dt: number, sample: GroundSampler, groundHeight: number | undefined): void {
    const throttle = this.throttleInput(this.moto.speed);
    const steer = this.steerInput(this.moto.speed, dt);
    const slide = this.keys.has('Space');
    this.motoThrottle = throttle;

    const spec = this.twoWheeler;
    this.measureSlopes(sample, dt, spec.geometry.wheelbase * 0.6, 0.35);
    this.offRoad = this.surfaceProbe !== undefined && !this.surfaceProbe(this.lat, this.lon);

    let result: { state: MotoState; yawDelta: number };
    if (this.airborne) {
      result = stepMotorbikeAir(this.moto, steer, dt, spec);
      // The throttle lifts the nose and the brake drops it; left alone, the
      // bike drifts toward its flight path, which is how it wants to land.
      const path = Math.atan2(this.vy, Math.max(1, Math.abs(this.moto.speed)));
      const pitchRate = throttle > 0 ? AIR_NOSE_UP : throttle < 0 ? -AIR_NOSE_DOWN : 0;
      this.airPitch += (pitchRate + (path - this.airPitch) * 1.5) * dt;
      this.airPitch = Math.max(-1.1, Math.min(1.1, this.airPitch));
    } else {
      result = stepMotorbike(this.moto, { throttle, steer, slide }, this.forwardSlope, dt,
        this.damaged(this.snowy(this.offRoad ? spec.dirt : spec.road, this.moto.speed < -0.1 || (throttle < 0 && this.moto.speed <= 0.2))), spec);
      this.airPitch = Math.atan(this.forwardSlope);
    }
    this.moto = result.state;
    this.moto = { ...this.moto, speed: this.dragged(this.moto.speed, dt) };
    this.sliding = slide && !this.airborne && Math.abs(this.moto.speed) > 2;
    this.braking = slide || (throttle < 0 && this.moto.speed > 0.2);
    this.yawRate = dt > 0 ? result.yawDelta / dt : 0;
    this.yaw -= result.yawDelta;

    const forward = this.horizontalForward();
    const lateral = this.moto.lateral ?? 0;
    const east = forward.x * this.moto.speed - forward.z * lateral;
    const southward = forward.z * this.moto.speed + forward.x * lateral;
    this.translate(east * dt, -southward * dt);

    for (const offset of [spec.circleOffset, -spec.circleOffset]) {
      const hit = this.collide(spec.radius, this.altitude + 0.3, this.altitude + 1.6, offset);
      if (hit === undefined) continue;
      if (hit.body !== undefined) {
        const side = this.moto.lateral ?? 0;
        const before = { x: forward.x * this.moto.speed - forward.z * side, z: forward.z * this.moto.speed + forward.x * side };
        if (!this.holds(hit, before)) {
          const after = this.shared(before, hit, offset, spec.radius, spec.strikeHeight, 0.4);
          if (after !== undefined) {
            this.moto = { ...this.moto, speed: after.x * forward.x + after.z * forward.z, lateral: -after.x * forward.z + after.z * forward.x };
            if (this.throwsRider(before, after, forward)) return;
          }
          continue;
        }
      }
      const into = (forward.x * hit.nx + forward.z * hit.nz) * Math.sign(this.moto.speed || 1);
      if (into < 0) {
        const side = this.moto.lateral ?? 0;
        const before = { x: forward.x * this.moto.speed - forward.z * side, z: forward.z * this.moto.speed + forward.x * side };
        const hard = -into * Math.abs(this.moto.speed) >= STOPS_DEAD;
        this.moto = { ...this.moto, speed: hard ? -this.moto.speed * 0.04 : this.moto.speed * (1 + into * 0.85), lateral: 0 };
        // Struck at the fender and the number board, over the front wheel.
        this.struck(before, this.moto.speed, forward, hit, offset, spec.radius, spec.strikeHeight);
        if (this.throwsRider(before, { x: forward.x * this.moto.speed, z: forward.z * this.moto.speed }, forward)) return;
      }
    }
    this.lastSpeed = Math.hypot(this.moto.speed, lateral);
    if (groundHeight === undefined) return;

    const wasAirborne = this.airborne;
    if (this.flyOrFollow(groundHeight, dt, sample, this.moto.speed)) {
      // Crossed up: the further the bike's pitch is from the ground's, the
      // more the landing costs. Level with a downslope, it rides away clean.
      const crossed = this.airPitch - Math.atan(this.forwardSlope);
      const miss = Math.abs(crossed);
      if (miss > CRASH_LANDING && -this.landing > CRASH_LANDING_SPEED) {
        // Nose first, over the bars; on the back wheel past its balance, off the back.
        const f = this.horizontalForward(), v = this.moto.speed;
        this.throwOff({ x: f.x * v, z: f.z * v }, this.landing, { x: f.x * v * 0.5, z: f.z * v * 0.5 }, f, crossed > 0);
        return;
      }
      const hard = Math.min(1, Math.max(0, -this.landing - 4) / 10);
      const keep = Math.max(0.4, 1 - 0.1 * hard - Math.max(0, miss - 0.35) * 0.9);
      this.moto = { ...this.moto, speed: this.moto.speed * keep, lean: this.moto.lean * 0.4, leanRate: 0 };
      this.airPitch = Math.atan(this.forwardSlope);
    } else if (this.airborne && !wasAirborne) {
      // Leaving a lip, the bike's speed was along the face: split it into
      // the climb and the carry at the face's angle, as it really leaves.
      const angle = Math.atan2(this.vy, Math.max(1, Math.abs(this.moto.speed)));
      this.vy *= Math.cos(angle);
      // Off a crest on the back wheel, it flies at the angle it left at.
      this.airPitch = angle + (this.moto.wheelie ?? 0);
      this.moto = { ...this.moto, speed: this.moto.speed * Math.cos(angle), wheelie: 0, wheelieRate: 0 };
    }
    this.easeDriveCamera(dt);
  }

  /**
   * The car follows the ground until the road bends away beneath it faster
   * than gravity can pull it down after it — the one way a real car leaves
   * the ground. Following a road, the car's vertical acceleration is its
   * speed times how fast its slope is changing; over a crest that is
   * downward, and when it exceeds g the tyres unload and it flies, on the
   * climb it had (speed × slope), until it comes down. A slow climb never
   * gets there; nor does a kerb or a step onto a road surface, since the slope
   * is the fitted plane under the whole car, not one height to the next.
   */
  private flyOrFollow(ground: number, dt: number, sample: GroundSampler, speed: number): boolean {
    if (dt <= 0) return false;
    const slope = this.forwardSlope;
    this.sinceLanding += dt;
    if (this.airborne) {
      this.airTime += dt;
      this.vy -= (this.mode === 'moto' ? GRAVITY * this.twoWheeler.airGravity : GRAVITY) * dt;
      this.altitude += this.vy * dt;
      if (this.altitude <= ground) {
        // Only a bike rides on up the last of a lip; a car meeting rising
        // ground lands on it, rather than being thrown up by it.
        if (this.airTime < LIP_GRACE && this.mode === 'moto') {
          // Still on the lip: carried up it at its climb, not landed.
          const cap = Math.max(1, BIKE_CLIMB * Math.abs(speed));
          const rise = Math.max(-cap, Math.min(cap, (ground - (this.lastPathHeight ?? ground)) / dt));
          const climb = this.mode === 'moto' ? rise * Math.cos(Math.atan2(rise, Math.max(1, Math.abs(speed)))) : rise;
          this.vy = Math.max(this.vy, climb);
          this.altitude = ground;
          this.lastPathHeight = ground;
          return false;
        }
        // Impact: how fast it meets the ground, the ground's own climb aside.
        this.landing = Math.min(0, this.vy - slope * speed);
        // A car meeting ground well above it lands where it is and rides up
        // onto it as on the road (`rideUp`), not lifted onto it at once.
        this.altitude = this.mode === 'moto' ? ground : this.rideUp(this.altitude, ground, speed, dt);
        this.airborne = false;
        this.vy = 0;
        this.sinceLanding = 0;
        this.pathVy = 0;
        this.lastPathHeight = ground;
        // The camera feels it: one jolt down, the harder the landing the
        // more, which its easing then takes back up.
        this.camHeight += Math.max(-0.7, this.landing * 0.09);
        return true;
      }
      return false;
    }
    // The ground where the car will be in a moment, along its heading.
    const f = this.horizontalForward();
    const cosLat = Math.max(1e-6, Math.cos(this.lat * DEG_TO_RAD));
    // A bike looks no further than a couple of metres: at speed, a car's
    // look ahead would see past a small kicker's lip from halfway up its face.
    const lookahead = this.mode === 'moto'
      ? TAKEOFF_LOOKAHEAD.map((t, i) => Math.min(t, BIKE_LOOKAHEAD[i] / Math.max(1, Math.abs(speed))))
      : TAKEOFF_LOOKAHEAD;
    const groundAhead = lookahead.map((t) => {
      const ahead = speed * t;
      return sample(
        this.lat + (-f.z * ahead) / METRES_PER_DEG_LAT,
        this.lon + (f.x * ahead) / (METRES_PER_DEG_LAT * cosLat),
      );
    });
    const bike = this.mode === 'moto';
    // Where it is: on the ground, or for a car still riding up onto it.
    const base = bike ? ground : this.rideUp(this.altitude, ground, speed, dt);
    if (leavesGround(speed, base, this.pathVy, groundAhead, this.sinceLanding, lookahead, bike ? undefined : CAR_TAKEOFF_MARGIN)) {
      this.airborne = true;
      this.airTime = 0;
      this.vy = bike ? this.pathVy : Math.min(this.pathVy, Math.min(CAR_CLIMB_MOST, CAR_CLIMB * Math.abs(speed)));
      this.altitude = base + this.vy * dt;
      this.airPitch = Math.atan2(this.vy, Math.max(1, Math.abs(speed)));
      return false;
    }
    this.altitude = base;
    // The machine's own vertical speed along its path, for the next step's
    // judgement. A bike is built to leave a jump's lip at its full angle, so
    // it reads the climb from the ground's height step to step. A car rides
    // on four wheels and its springs: its climb is its speed up the slope of
    // the ground under the whole of it, so a kerb, a step onto a road or the
    // edge of a bridge deck is no climb at all, and cannot throw it into the
    // air a moment later.
    const cap = Math.max(1, BIKE_CLIMB * Math.abs(speed));
    const carCap = Math.min(CAR_CLIMB_MOST, CAR_CLIMB * Math.abs(speed));
    const rise = !bike ? Math.max(-carCap, Math.min(carCap, speed * slope))
      : this.lastPathHeight === undefined ? 0 : Math.max(-cap, Math.min(cap, (ground - this.lastPathHeight) / dt));
    this.pathVy += (rise - this.pathVy) * (1 - Math.exp(-dt * 20));
    this.lastPathHeight = ground;
    return false;
  }

  /**
   * Where a car at `altitude` stands, with the ground under it at `ground`:
   * on it, but up it no faster than its wheels can climb. Up a kerb at once,
   * up a real road's steepest at its speed; not up a spike, or the edge of a
   * bridge deck the ground reads for a moment, which would lift it metres
   * and drop it off the other side. Standing or barely moving, it is where
   * its ground is: just placed there, or set down. A bike, light and built
   * for it, follows the ground as it is (see `flyOrFollow`).
   */
  private rideUp(altitude: number, ground: number, speed: number, dt: number): number {
    const lift = ground - altitude;
    if (lift <= CAR_STEP || Math.abs(speed) < 3) return ground;
    return altitude + Math.min(lift, (CAR_STEP_RATE + CAR_RIDE_UP * Math.abs(speed)) * dt);
  }

  /**
   * Ease the drive camera toward where it should be: behind the car's heading
   * (or its slide, a little), further back and wider-angled with speed. The
   * mouse's look offset drifts back to centre once the mouse rests.
   */
  private easeDriveCamera(dt: number): void {
    const bike = this.mode === 'moto';
    const machine = bike ? this.moto : this.drive;
    const speed = Math.abs(machine.speed);
    this.sinceLook += dt;
    if (this.sinceLook > 1.2 && speed > 3) this.lookYaw *= Math.exp(-dt * 1.4);
    // Look slightly into a slide rather than straight down the bonnet.
    const slip = Math.atan2(machine.lateral ?? 0, Math.max(1, speed));
    const want = this.yaw + this.lookYaw - slip * 0.5;
    const delta = Math.atan2(Math.sin(want - this.camYaw), Math.cos(want - this.camYaw));
    this.camYaw += delta * (1 - Math.exp(-dt * (3 + speed * 0.08)));
    // The bike is small: the camera sits closer, and pulls back further with speed.
    // A truck's camera sits back by its length, its trailer's too.
    const rig = bike ? 0 : Math.max(0, this.rigLength - VEHICLE.length) * 0.8;
    const distance = bike ? 4.3 + Math.min(2.6, speed * 0.075) : 6.4 + rig + Math.min(3, speed * 0.07);
    this.camDistance += (distance - this.camDistance) * (1 - Math.exp(-dt * 2));
    // Height eased: the camera rides behind the car, not bolted to its roof.
    // A landing's jolt pulls it down for a moment and lets go.
    this.camHeight += (this.altitude - this.camHeight) * (1 - Math.exp(-dt * 7));
    if (Math.abs(this.altitude - this.camHeight) > 20) this.camHeight = this.altitude;
    const fov = 60 + Math.min(14, speed * 0.32);
    this.camFov += (fov - this.camFov) * (1 - Math.exp(-dt * 2));
  }

  /**
   * Sample the terrain around the car to find the gradient it is sitting on.
   *
   * Four samples a wheelbase apart, rather than the terrain normal under a
   * single point: the car should ride the ground it actually spans, so one
   * wheel in a ditch tilts it and a sharp ridge does not spike it.
   */
  private measureSlopes(sample: GroundSampler, dt: number, halfL: number, halfW: number): void {
    const cosLat = Math.max(1e-6, Math.cos(this.lat * DEG_TO_RAD));
    const forward = this.horizontalForward();
    // Scene forward is (x = east, z = south), so north is -z.
    const fEast = forward.x;
    const fNorth = -forward.z;

    const at = (east: number, north: number): number | undefined =>
      sample(
        this.lat + north / METRES_PER_DEG_LAT,
        this.lon + east / (METRES_PER_DEG_LAT * cosLat),
      );

    // The plane that best fits the ground under the whole car: nine samples
    // over a footprint a little larger than the wheels, least squares. The
    // terrain is flat triangles fourteen metres across, and four samples
    // under the wheels see its slope jump at every triangle edge; a fitted
    // plane over nine sees the ground as the tyres and springs would.
    let su = 0, sv = 0;
    for (const u of [-1, 0, 1]) {
      for (const v of [-1, 0, 1]) {
        // u forward, v right; right of forward (east, north) is (north, -east).
        const h = at(fEast * u * halfL + fNorth * v * halfW, fNorth * u * halfL - fEast * v * halfW);
        // Keep the previous value when a sample is missing: a half-loaded
        // chunk should not flip the car onto its roof.
        if (h === undefined) return;
        su += u * h; sv += v * h;
      }
    }
    // On a symmetric 3 × 3 grid the fit separates: Σu² = Σv² = 6.
    const forwardSlope = su / 6 / halfL;
    const lateralSlope = sv / 6 / halfW;
    // And the ground's slope changes are eased in over a fraction of a
    // second, as a car on its springs takes them — also for the physics, so
    // gravity along the slope does not jerk the speed at each facet.
    const k = 1 - Math.exp(-dt * 10);
    this.forwardSlope += (forwardSlope - this.forwardSlope) * k;
    this.lateralSlope += (lateralSlope - this.lateralSlope) * k;
  }

  private updateWalk(dt: number, move: Vector3, groundHeight: number | undefined): void {
    const running = this.keys.has('ShiftLeft') || this.keys.has('ShiftRight') || this.autopilot?.run === true;
    // Wading through deep snow: knee-deep halves the pace.
    const speed = (running ? RUN_SPEED : WALK_SPEED) * wading(this.snowLying);
    // Ease the velocity toward what the keys ask for. A body that reaches a
    // run in one frame and stops dead in the next reads as a camera on rails,
    // and it would make the gait snap between standing and full stride. In
    // the air it keeps its momentum, with only a little steering.
    const wantEast = move.x * speed;
    const wantNorth = -move.z * speed;
    const step = FOOT_ACCELERATION * dt * (this.airborne ? AIR_CONTROL : 1);
    const dEast = wantEast - this.footEast;
    const dNorth = wantNorth - this.footNorth;
    const gap = Math.hypot(dEast, dNorth);
    if (gap <= step || gap < 1e-9) {
      this.footEast = wantEast;
      this.footNorth = wantNorth;
    } else {
      this.footEast += (dEast / gap) * step;
      this.footNorth += (dNorth / gap) * step;
    }

    this.translate(this.footEast * dt, this.footNorth * dt);
    let feet = this.altitude - EYE_HEIGHT;
    // Walls, trunks and posts push back; the velocity into them goes.
    const hit = this.collide(BODY_RADIUS, feet + 0.25, feet + EYE_HEIGHT);
    if (hit !== undefined) {
      // Normal (nx, nz) is in scene axes: east is x, north is -z.
      const into = this.footEast * hit.nx - this.footNorth * hit.nz;
      if (into < 0) { this.footEast -= into * hit.nx; this.footNorth += into * hit.nz; }
    }
    const moving = Math.hypot(this.footEast, this.footNorth);
    this.lastSpeed = moving;
    // Turn the body toward the direction of travel — scene yaw for a heading
    // of (east, north) is atan2(-east, north).
    if (moving > 0.2 && !this.airborne) this.turnBody(Math.atan2(-this.footEast, this.footNorth), dt);
    if (groundHeight === undefined) return;

    if (this.airborne) {
      this.vy -= GRAVITY * dt;
      feet += this.vy * dt;
      // Down onto the ground — or onto anything within a step, on the way down.
      if (feet <= groundHeight && this.vy <= 0) {
        // Off a roof or a bridge: too hard a landing to take standing. Not
        // when the ground went from under someone standing still: that is
        // the world streaming in finer under them, not a fall.
        if (-this.vy > HARD_LANDING && !this.groundFell) this.fell = { x: this.footEast, y: this.vy, z: -this.footNorth };
        feet = groundHeight;
        this.airborne = false;
        this.vy = 0;
        if (this.fell !== undefined) this.goDown();
      }
    } else if (groundHeight < feet - STEP_DOWN) {
      // Walked off an edge: a bridge, a raised road, a bank. Fall.
      this.airborne = true;
      this.vy = 0;
      this.groundFell = moving < 0.3;
    } else {
      feet = groundHeight;
    }
    this.altitude = feet + EYE_HEIGHT;
  }

  private turnBody(target: number, dt: number): void {
    const delta = Math.atan2(Math.sin(target - this.bodyYaw), Math.cos(target - this.bodyYaw));
    this.bodyYaw += Math.sign(delta) * Math.min(Math.abs(delta), TURN_RATE * dt);
  }

  // Reaching the ground from a parachute drop always ends on foot.
  private updateFreefall(dt: number, move: Vector3, groundHeight: number | undefined): void {
    const agl = groundHeight === undefined ? Infinity : this.altitude - groundHeight;
    this.freefallAgl = agl;
    // Never dived, it opens by itself low down; dived, it is the skydiver's to pull.
    if (!this.dived && agl < CANOPY_ALTITUDE && this.pendingSpawnAgl === undefined) this.canopyPulled = true;
    const underCanopy = this.canopyPulled;
    this.underCanopy = underCanopy;
    this.turnBody(this.yaw, dt);

    // W held in freefall is a dive: head down, straight ahead, fast.
    const diving = !underCanopy && (this.keys.has('KeyW') || this.pad.moveY > 0.6);
    if (diving) this.dived = true;
    const target = diving ? 1 : 0;
    this.dive += Math.sign(target - this.dive) * Math.min(Math.abs(target - this.dive), DIVE_RATE * dt);
    if (underCanopy) this.dive = 0;

    const wanted = underCanopy ? CANOPY_SPEED : FREEFALL_SPEED + (DIVE_FALL_SPEED - FREEFALL_SPEED) * this.dive;
    // The canopy's opening shock takes the speed off at once; a dive builds.
    this.fallSpeed = underCanopy ? wanted : this.fallSpeed + (wanted - this.fallSpeed) * Math.min(1, 1.5 * dt);
    const glide = underCanopy ? CANOPY_GLIDE : FREEFALL_GLIDE + (DIVE_GLIDE - FREEFALL_GLIDE) * this.dive;

    this.translate(move.x * glide * dt, -move.z * glide * dt);
    this.altitude -= this.fallSpeed * dt;
    this.lastSpeed = Math.hypot(this.fallSpeed, move.lengthSq() > 0 ? glide : 0);

    if (groundHeight !== undefined && this.altitude <= groundHeight + EYE_HEIGHT) {
      // Touchdown; without a canopy, a body that falls rather than lands.
      const crashed = !this.underCanopy && this.fallSpeed > HARD_LANDING;
      if (crashed) this.fell = { x: move.x * FREEFALL_GLIDE, y: -this.fallSpeed, z: move.z * FREEFALL_GLIDE };
      this.altitude = groundHeight + EYE_HEIGHT;
      this.mode = 'walk';
      this.lastSpeed = 0;
      this.underCanopy = false;
      this.canopyPulled = false;
      this.dived = false;
      this.dive = 0;
      this.fallSpeed = FREEFALL_SPEED;
      this.footEast = 0;
      this.footNorth = 0;
      this.airborne = false;
      this.vy = 0;
      if (crashed) this.goDown();
    }
  }

  /** Move by a local east/north offset in metres, back into lat/lon. */
  private translate(east: number, north: number): void {
    this.lat += north / METRES_PER_DEG_LAT;
    const cosLat = Math.cos(this.lat * DEG_TO_RAD);
    // Guard the pole: cos(lat) -> 0 makes the longitude step explode.
    if (Math.abs(cosLat) > 1e-6) {
      this.lon += east / (METRES_PER_DEG_LAT * cosLat);
    }
    this.lat = Math.min(85, Math.max(-85, this.lat));
    if (this.lon > 180) this.lon -= 360;
    if (this.lon < -180) this.lon += 360;
  }

  /** Camera forward flattened onto the ground plane. */
  private horizontalForward(): Vector3 {
    return new Vector3(-Math.sin(this.yaw), 0, -Math.cos(this.yaw));
  }

  /**
   * Place the camera in the scene.
   *
   * The scene origin tracks the ENU origin, so the camera is placed by the
   * caller converting the player's coordinates; the world hands us the offset
   * each frame via `setLocalPosition`.
   */
  private localEast = 0;
  private localNorth = 0;

  setLocalPosition(east: number, north: number): void {
    this.localEast = east;
    this.localNorth = north;
    this.syncCamera();
  }

  /** Looking straight down on the player from above, north up: see `view`. */
  get topDown(): boolean {
    return this.view === 'top';
  }

  /** Zoom the top-down view in (factor under 1) or out. */
  zoomTop(factor: number): void {
    this.topHeight = Math.max(TOP_LOWEST, Math.min(TOP_HIGHEST, this.topHeight * factor));
  }

  /** Place the camera for the view, then shake it if a crash has jolted it. */
  private syncCamera(): void {
    this.placeCamera();
    if (this.shakeLeft <= 0 || this.view === 'top') return;
    // Thrown, then settling: falling off as the square of the time left.
    const k = this.shakeAmplitude * (this.shakeLeft / this.shakeSeconds) ** 2, t = this.shakeClock;
    this.camera.position.x += Math.sin(t * 53) * k;
    this.camera.position.y += Math.sin(t * 41 + 1) * k * 0.7;
    this.camera.position.z += Math.sin(t * 47 + 2) * k;
  }

  private placeCamera(): void {
    if (this.view === 'top') {
      // Straight down from above, the top of the screen north: a map of the
      // ground round the player that moves with them.
      this.setFov(45);
      this.camera.up.set(0, 0, -1);
      this.camera.position.set(this.localEast, this.altitude + this.topHeight, -this.localNorth);
      this.camera.lookAt(this.localEast, this.altitude - EYE_HEIGHT, -this.localNorth);
      return;
    }
    this.camera.up.set(0, 1, 0);
    if (this.mode === 'drive' || this.mode === 'moto') {
      this.syncDriveCamera();
      return;
    }
    this.setFov(60);
    if (this.mode === 'heli') {
      this.syncChaseCamera(17 * this.zoom, 6.5 * (1 + (this.zoom - 1) * 0.7), -0.2);
      return;
    }
    // Thrown off, the camera watches the body, whatever the view.
    if (this.chase || this.down) {
      if (this.mode === 'freefall') {
        // Look down over a skydiver's back, and up under a canopy.
        // In a dive the camera drops in behind, looking down the line of it.
        this.syncFollowCamera((this.underCanopy ? 10.5 : 7.5 + this.dive * 1.5) * this.zoom,
          this.underCanopy ? 2.6 : 0.9 + this.dive * 1.2, this.underCanopy ? 0.05 : -0.7 - this.dive * 0.35);
      } else {
        // Further back, and looking down a touch more, so the wider view
        // shows the ground round the character rather than more sky.
        this.syncFollowCamera(FOLLOW_DISTANCE * this.zoom, FOLLOW_TARGET, -(this.zoom - 1) * 0.25);
      }
      return;
    }
    this.camera.position.set(this.localEast, this.altitude, -this.localNorth);
    this.euler.set(this.pitch, this.yaw, 0);
    this.camera.quaternion.setFromEuler(this.euler);
  }

  /**
   * Third-person orbit around the player's body.
   *
   * The mouse steers the camera and the keys move relative to it, so the
   * character turns to where the player wants to go rather than the player
   * steering the character. The camera is kept above the terrain behind it —
   * on a hillside an orbit of fixed radius would otherwise dip underground
   * every time the player looked up.
   */
  private syncFollowCamera(distance: number, targetHeight: number, tilt: number): void {
    const feet = this.altitude - EYE_HEIGHT;
    const tx = this.localEast;
    const ty = feet + targetHeight;
    const tz = -this.localNorth;
    const pitch = Math.max(-1.45, Math.min(1.2, this.pitch + tilt));
    const cosPitch = Math.cos(pitch);
    const dirX = -Math.sin(this.yaw) * cosPitch;
    const dirY = Math.sin(pitch);
    const dirZ = -Math.cos(this.yaw) * cosPitch;

    let cx = tx - dirX * distance;
    let cy = ty - dirY * distance;
    let cz = tz - dirZ * distance;

    if (this.sampler !== undefined) {
      const cosLat = Math.max(1e-6, Math.cos(this.lat * DEG_TO_RAD));
      const ground = this.sampler(
        this.lat + -(cz - tz) / METRES_PER_DEG_LAT,
        this.lon + (cx - tx) / (METRES_PER_DEG_LAT * cosLat),
      );
      const floor = ground === undefined ? undefined : ground + 0.45;
      if (floor !== undefined && cy < floor) {
        // Pull in toward the character rather than just lifting, so the view
        // stays aimed at them instead of looking down on their head.
        const lift = floor - cy;
        cy = floor;
        const pull = Math.min(0.6, lift / Math.max(1, distance));
        cx = tx + (cx - tx) * (1 - pull);
        cz = tz + (cz - tz) * (1 - pull);
      }
    }
    cy = this.underRoof(cx, cz, cy, feet);

    this.camera.position.set(cx, cy, cz);
    this.camera.lookAt(tx, ty, tz);
  }

  private setFov(fov: number): void {
    if (Math.abs(this.camera.fov - fov) < 0.05) return;
    this.camera.fov = fov;
    this.camera.updateProjectionMatrix();
  }

  /**
   * The car's cameras. Chase: trailing the car on its eased yaw, low and
   * close enough to see the road, looking a little ahead of the bonnet, kept
   * above the ground behind. Driver's seat: at the wheel, on the left.
   */
  private syncDriveCamera(): void {
    const carY = this.camHeight;
    const bike = this.mode === 'moto';
    if (!this.chase && bike) {
      // In the helmet: over the bars, leaning with the bike — the head keeps
      // itself a little more upright than the bike does — and pitched with it.
      this.setFov(72);
      const lean = this.moto.lean;
      const fx = -Math.sin(this.yaw), fz = -Math.cos(this.yaw);
      const eye = this.twoWheeler.geometry.eyeHeight;
      const side = Math.sin(lean) * eye * 0.8;
      // The head is over the tank, ahead of the bike's middle.
      this.camera.position.set(this.localEast - fz * side + fx * 0.18, carY + Math.cos(lean) * eye, -this.localNorth + fx * side + fz * 0.18);
      this.euler.set(this.pitch * 0.6 - 0.12 + (this.airPitch + (this.moto.wheelie ?? 0)) * 0.8, this.yaw + this.lookYaw, -lean * 0.6);
      this.camera.quaternion.setFromEuler(this.euler);
      return;
    }
    if (!this.chase) {
      this.setFov(66);
      const yaw = this.yaw + this.lookYaw;
      // The seat is to the left of the car's centre line: right of forward is (-fz, fx).
      const fx = -Math.sin(this.yaw), fz = -Math.cos(this.yaw);
      const { side, eye, back } = this.carSeat;
      this.camera.position.set(this.localEast - fz * side - fx * back, carY + eye, -this.localNorth + fx * side - fz * back);
      this.euler.set(this.pitch * 0.6 + Math.atan(this.forwardSlope), yaw, 0);
      this.camera.quaternion.setFromEuler(this.euler);
      return;
    }
    this.setFov(this.camFov);
    const speed = Math.abs(bike ? this.moto.speed : this.drive.speed);
    const back = new Vector3(Math.sin(this.camYaw), 0, Math.cos(this.camYaw));
    // The mouse raises and lowers the camera round the car.
    const lift = Math.max(-0.5, Math.min(4, -this.pitch * 5));
    const tall = !bike && this.chassis !== CAR_CHASSIS;
    const height = (bike ? 1.75 : tall ? 4.2 + this.rigLength * 0.08 : 2.2) * (1 + (this.zoom - 1) * 0.8) + lift + Math.min(0.8, speed * 0.02);
    const cx = this.localEast + back.x * this.camDistance * this.zoom;
    const cz = -this.localNorth + back.z * this.camDistance * this.zoom;
    let cy = carY + height;
    if (this.sampler !== undefined) {
      const cosLat = Math.max(1e-6, Math.cos(this.lat * DEG_TO_RAD));
      const ground = this.sampler(
        this.lat + (-(cz + this.localNorth)) / METRES_PER_DEG_LAT,
        this.lon + (cx - this.localEast) / (METRES_PER_DEG_LAT * cosLat),
      );
      if (ground !== undefined && cy < ground + 0.8) cy = ground + 0.8;
    }
    cy = this.underRoof(cx, cz, cy, carY);
    this.camera.position.set(cx, cy, cz);
    // Look at a point over the car and a little ahead of it, further ahead
    // the faster it goes.
    const ahead = 2 + Math.min(6, speed * 0.18);
    const fx = -Math.sin(this.camYaw), fz = -Math.cos(this.camYaw);
    this.camera.lookAt(this.localEast + fx * ahead, carY + (bike ? 1.0 : tall ? 2.2 : 1.1), -this.localNorth + fz * ahead);
    // On the bike the horizon tips a little with the lean.
    if (bike) this.camera.rotateZ(-this.moto.lean * 0.14);
  }

  /**
   * Chase camera, shared by the car and the helicopter.
   *
   * The mouse steers a look direction *relative to the vehicle* rather than an
   * absolute one, so glancing sideways at a junction does not leave the camera
   * pointing the wrong way once you move on.
   */
  private syncChaseCamera(distance: number, height: number, tilt: number): void {
    const yaw = this.yaw + this.lookYaw;
    const back = new Vector3(Math.sin(yaw), 0, Math.cos(yaw));

    if (this.chase) {
      this.camera.position.set(
        this.localEast + back.x * distance,
        this.altitude + height,
        -this.localNorth + back.z * distance,
      );
      this.euler.set(this.pitch + tilt, yaw, 0);
    } else {
      if (this.mode === 'heli') {
        // In the pilot's seat: left of the middle and ahead of the mast.
        const across = HELI_SEAT.x, back = HELI_SEAT.hipZ - 0.05;
        this.camera.position.set(
          this.localEast + Math.cos(this.yaw) * across + Math.sin(this.yaw) * back,
          this.altitude - HELI.skidHeight + HELI_SEAT.hipY + 0.78,
          -this.localNorth - Math.sin(this.yaw) * across + Math.cos(this.yaw) * back,
        );
      } else {
        this.camera.position.set(this.localEast, this.altitude + VEHICLE.eyeHeight, -this.localNorth);
      }
      this.euler.set(this.pitch, yaw, 0);
    }
    this.camera.quaternion.setFromEuler(this.euler);
  }

  /**
   * Press or release a key from somewhere other than the keyboard — the touch
   * controls. Same codes and the same effect as the physical key.
   */
  virtualKey(code: string, down: boolean): void {
    if (!down) {
      this.keys.delete(code);
      return;
    }
    if (!this.inputEnabled) return;
    this.keys.add(code);
    this.press(code);
  }

  /** Turn the view by a pointer movement in pixels, as the mouse does. */
  lookBy(dx: number, dy: number): void {
    if (!this.inputEnabled) return;
    if (this.mode === 'drive' || this.mode === 'moto' || this.mode === 'heli') {
      this.sinceLook = 0;
      // The controls own the machine's yaw, so the pointer only moves the view.
      this.lookYaw -= dx * LOOK_SENSITIVITY;
      this.lookYaw = Math.max(-Math.PI, Math.min(Math.PI, this.lookYaw));
    } else {
      this.yaw -= dx * LOOK_SENSITIVITY;
    }
    this.pitch -= dy * LOOK_SENSITIVITY;
    const limit = Math.PI / 2 - 0.01;
    this.pitch = Math.min(limit, Math.max(-limit, this.pitch));
  }

  /**
   * Get into a vehicle standing at (lat, lon, altitude of its ground) facing
   * `yaw`: a car, or a motorbike. The caller has played the getting-in.
   */
  enterVehicle(mode: 'drive' | 'moto', lat: number, lon: number, ground: number, yaw: number,
    seat?: { side: number; eye: number; back: number }): void {
    this.lat = lat;
    this.lon = lon;
    this.altitude = ground;
    this.yaw = yaw;
    if (seat !== undefined) this.carSeat = seat;
    if (this.mode !== mode) this.press(mode === 'drive' ? 'KeyV' : 'KeyB', true);
  }

  /**
   * Step out of the vehicle, onto the ground at (lat, lon) with the feet at
   * `feet`, facing `bodyYaw`. The vehicle stays where it stopped: that is for
   * the caller to draw.
   */
  exitVehicle(lat: number, lon: number, feet: number, bodyYaw: number): void {
    if (this.mode !== 'drive' && this.mode !== 'moto') return;
    this.press(this.mode === 'drive' ? 'KeyV' : 'KeyB', true);
    this.lat = lat;
    this.lon = lon;
    this.altitude = feet + EYE_HEIGHT;
    this.bodyYaw = bodyYaw;
    this.airborne = false;
    this.vy = 0;
  }

  /** In the helicopter with its skids on the ground and all but stopped: it may be got out of. */
  get helicopterParked(): boolean {
    return this.mode === 'heli' && this.heliFloor !== undefined && this.altitude <= this.heliFloor + 0.05 && this.lastSpeed < 1.5;
  }

  /**
   * Get into a helicopter standing on the ground at (lat, lon, altitude of
   * that ground), facing `yaw`.
   */
  enterHelicopter(lat: number, lon: number, ground: number, yaw: number): void {
    this.lat = lat;
    this.lon = lon;
    this.yaw = yaw;
    if (this.mode !== 'heli') this.press('KeyH', true);
    this.altitude = ground + HELI.skidHeight;
    this.heliFloor = this.altitude;
  }

  /** Step out of the helicopter onto the ground at (lat, lon), feet at `feet`, facing `bodyYaw`. */
  exitHelicopter(lat: number, lon: number, feet: number, bodyYaw: number): void {
    if (this.mode !== 'heli') return;
    this.press('KeyH', true);
    this.lat = lat;
    this.lon = lon;
    this.altitude = feet + EYE_HEIGHT;
    this.bodyYaw = bodyYaw;
    this.airborne = false;
    this.vy = 0;
  }

  /** One-shot actions bound to a key going down. `forced` is a switch made by `enterVehicle`, not a key. */
  private press(code: string, forced = false): void {
    // Down, only the view can be changed.
    if (this.down && code !== 'KeyC') return;
    // A press the listener took care of goes no further.
    if (!forced && this.onPress?.(code) === true) return;
    const summon = forced || this.summonVehicles;
    // V gets in and out of the car. Entering from a fall is not allowed —
    // the car has to be on the ground.
    if (code === 'KeyV' && this.mode !== 'freefall' && summon) {
      if (!forced && this.mode !== 'drive') this.carSeat = { side: VEHICLE.seatOffset, eye: VEHICLE.eyeHeight, back: 0 };
      this.mode = this.mode === 'drive' ? 'walk' : 'drive';
      this.airborne = false;
      this.vy = 0;
      this.pathVy = 0;
      this.lastPathHeight = undefined;
      this.camYaw = this.yaw;
      this.camDistance = 7;
      this.camHeight = this.altitude;
      this.bodyYaw = this.yaw;
      this.footEast = 0;
      this.footNorth = 0;
      this.heli = HOVER;
      this.drive = { speed: 0, steerAngle: 0 };
      this.lookYaw = 0;
      this.lastSpeed = 0;
    }

    // B gets on and off the motorbike, on the same terms as the car.
    if (code === 'KeyB' && this.mode !== 'freefall' && summon) {
      this.mode = this.mode === 'moto' ? 'walk' : 'moto';
      this.airborne = false;
      this.vy = 0;
      this.pathVy = 0;
      this.lastPathHeight = undefined;
      this.camYaw = this.yaw;
      this.camDistance = 5;
      this.camHeight = this.altitude;
      this.bodyYaw = this.yaw;
      this.footEast = 0;
      this.footNorth = 0;
      this.heli = HOVER;
      this.moto = UPRIGHT;
      this.airPitch = 0;
      this.lookYaw = 0;
      this.lastSpeed = 0;
    }

    // H conjures a helicopter and leaves it again, on the same terms as the
    // car. Otherwise helicopters stand on helipads: see `enterHelicopter`.
    if (code === 'KeyH' && this.mode !== 'freefall' && summon) {
      this.mode = this.mode === 'heli' ? 'walk' : 'heli';
      this.airborne = false;
      this.vy = 0;
      this.bodyYaw = this.yaw;
      this.footEast = 0;
      this.footNorth = 0;
      this.heli = HOVER;
      this.lookYaw = 0;
      this.climbRate = 0;
      this.lastSpeed = 0;
    }

    // The top-down view's zoom, from the touch buttons.
    if (code === 'TopZoomIn') this.zoomTop(1 / 1.35);
    if (code === 'TopZoomOut') this.zoomTop(1.35);

    // C cycles the views — close behind, further back, first person — on
    // foot as well as in a vehicle.
    if (code === 'KeyC') {
      this.view = this.view === 'near' ? 'far' : this.view === 'far' ? 'first' : this.view === 'first' ? 'top' : 'near';
      // From above, a click on the ground is where to go: the pointer is let go.
      if (this.view === 'top' && document.pointerLockElement !== null) document.exitPointerLock();
      // Into first person at once; back out from close in.
      if (this.view === 'first') this.zoom = 1;
    }

    // Space jumps. In the car and on the bike it is the handbrake, in the
    // helicopter the collective, all handled in their updates.
    if (code === 'Space' && this.mode === 'walk' && !this.airborne) {
      this.airborne = true;
      this.vy = JUMP_SPEED;
      this.groundFell = false;
    }

    // P is the parachute: from the ground, up into the air to drop again;
    // falling, it opens the canopy now, as Space does. Left alone it opens by
    // itself at `CANOPY_ALTITUDE`, unless the skydiver has dived.
    if (code === 'KeyP' && this.mode === 'walk') {
      this.mode = 'freefall';
      this.altitude += 1500;
      this.airborne = false;
      this.vy = 0;
      this.canopyPulled = false;
      this.dived = false;
      this.dive = 0;
      this.fallSpeed = FREEFALL_SPEED;
    } else if ((code === 'KeyP' || code === 'Space') && this.mode === 'freefall' && this.pendingSpawnAgl === undefined) {
      this.canopyPulled = true;
    }
  }

  private bindInput(): void {
    const el = this.domElement;
    // A touch has no pointer to lock; the touch controls do the looking.
    let lastPointer = 'mouse';
    el.addEventListener('pointerdown', (event) => { lastPointer = event.pointerType; });
    el.addEventListener('click', () => {
      if (this.pointerLocked || lastPointer !== 'mouse' || this.view === 'top') return;
      // Browsers refuse a lock requested within about a second of leaving one
      // (Esc, or the world map releasing it) and reject the promise — older
      // ones return nothing. Either way the next click simply asks again, so
      // the refusal is swallowed rather than surfacing as an uncaught error.
      const request = el.requestPointerLock() as Promise<void> | undefined;
      request?.catch(() => {});
    });
    document.addEventListener('pointerlockchange', () => {
      this.pointerLocked = document.pointerLockElement === el;
    });
    document.addEventListener('mousemove', (event) => {
      if (this.pointerLocked) this.lookBy(event.movementX, event.movementY);
    });
    window.addEventListener('keydown', (event) => this.virtualKey(event.code, true));
    window.addEventListener('keyup', (event) => this.virtualKey(event.code, false));
    window.addEventListener('blur', () => this.keys.clear());
  }
}
